import { inject, Injectable } from '@angular/core';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { CtTopBarButtonsComponent } from '@cloud-twin-v2/ct-top-bar-buttons/ct-top-bar-buttons.component';
import {
  CtWorkspaceDto,
  ElementTypeCategoriesDto,
  LayerIconDto,
  LayerStylesDto,
  LayerThematicStylesDto,
  LayerType,
  WorkspaceConnectSettingDto,
  WorkspaceMapSettingsDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { ScenarioItem } from '@cloud-twin-v2/scenarios-configuration/scenario-item';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { SettingsService } from '@common-modules/shared/config/settings.service';
import { MapTopbarButtonsConfiguration } from '@common-modules/wlm-map/components/map-top-bar-buttons/map-top-bar-buttons.component';
import { MapSidePanelSettings } from '@common-modules/wlm-map/components/side-panel/map-side-panel-settings';
import { GenericMapSettings } from '@common-modules/wlm-map/generic-map/models/generic-map-settings';
import { GeoserverLayerProviderSettings } from '@common-modules/wlm-map/generic-map/models/geoserver-map-provider-settings';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import {
  LayerDefinition,
  LayerProtocol,
  MapStyleDefinition,
} from '@common-modules/wlm-map/generic-map/models/layer-type';
import { MapGenericStyle } from '@common-modules/wlm-map/generic-map/models/map-generic-style';
import { forkJoin, map, Observable, of, ReplaySubject, switchMap, take } from 'rxjs';
import { CtMapModule } from '../ct-map.module';
import { CTMapFiltrableProperties } from '../models/ct-map-filtrable-properties';
import { DEFAULT_LAYER_STYLES } from '../models/default-layer-styles';
import { CTMapPersistencyService } from './ct-map-persistency.service';
import { CtMapThematicsService } from './ct-map-thematics.service';

export class BuildMapSettingsParams {
  scenarioVisibilityChange$: ReplaySubject<ScenarioItem>;
  namespacesChange$: ReplaySubject<Partial<CTMapFiltrableProperties>>;
}

@Injectable()
export class CTMapBuilderService {
  private readonly _ctAttributes = inject(CtAttributesService);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _settingsService = inject(SettingsService);
  private readonly _ctMapPersistencyService = inject(CTMapPersistencyService);
  private readonly _ctMapThematicsService = inject(CtMapThematicsService);

  buildMapSettings(params: BuildMapSettingsParams): Observable<GenericMapSettings> {
    return this._selectedWorkspaceService.selectedWorkspace$.pipe(
      switchMap((workspace) => {
        if (!workspace) {
          return of(null);
        }

        const connectSettings = workspace.workspaceConnectSettings;
        const allScenarioIds = this.getAllScenarioIds(connectSettings);

        return forkJoin([
          this._ctAttributes.getDateRange(allScenarioIds),
          this._ctAttributes.metadata$.pipe(take(1)),
          this._ctAttributes.getStylesByNEType().pipe(take(1)),
          this._ctAttributes.getIconsByNEType().pipe(take(1)),
          this.getLayerProvider(),
          this._ctMapPersistencyService.settings$.pipe(take(1)),
          this._ctMapThematicsService.getCurrentThematics(),
          this._selectedWorkspaceService.selectedWorkspace$.pipe(take(1)),
        ]).pipe(
          map(
            ([
              dateRange,
              metadata,
              stylesByNEType,
              iconsByNEType,
              layerProvider,
              persistencySettings,
              currentThematics,
              selectedWorkspace,
            ]) => {
              const layerItems = this.buildLayerItems(
                metadata,
                stylesByNEType,
                iconsByNEType,
                layerProvider,
                persistencySettings,
                currentThematics,
                selectedWorkspace
              );
              const mapSettings: GenericMapSettings = {
                layerItems,
                dateRange,
                sidePanelSettings: this.buildSidePanel(),
                topBarSettings: this.buildTopBarConfiguration(params),
              };

              return mapSettings;
            }
          )
        );
      })
    );
  }

  private buildLayerItems(
    metadata: CtMetadata,
    stylesByNEType: Map<string, LayerStylesDto[]>,
    iconsByNEType: Map<string, LayerIconDto[]>,
    layerProvider: GeoserverLayerProviderSettings,
    persistencySettings: WorkspaceMapSettingsDto,
    currentThematics: LayerThematicStylesDto[],
    selectedWorkspace: CtWorkspaceDto
  ): LayerItemV2[] {
    let layerItems: LayerItemV2[] = [];

    const scenariosByNeType = new Map<string, string[]>();
    const geometryByNeType = new Map<string, LayerType>();

    for (const [scenarioId, elementTypeCategories] of Object.entries(metadata)) {
      for (const elementTypeCategory of elementTypeCategories) {
        if (!this.isEmptyNEType(elementTypeCategory)) {
          const type = elementTypeCategory.type;

          const existingScenarioIds = scenariosByNeType.get(type) ?? [];
          existingScenarioIds.push(scenarioId);
          scenariosByNeType.set(type, existingScenarioIds);

          geometryByNeType.set(type, (elementTypeCategory as any).geometryType);
        }
      }
    }

    for (let [neType, scenarios] of scenariosByNeType.entries()) {
      const layerType = geometryByNeType.get(neType);
      const layerDefinition = new LayerDefinition({
        layerType,
        layerProtocol: LayerProtocol.WFS,
      });

      const styleDtos = stylesByNEType.get(neType);
      const availableStyles = this.mapToGenericStyles(styleDtos);
      const layerStyle = availableStyles.length
        ? availableStyles[0]
        : DEFAULT_LAYER_STYLES.get(layerType);

      const filtrableProperties = new CTMapFiltrableProperties({
        neType,
        namespaces: scenarios.map((scenarioId) => ({ scenarioId })),
        layerIds: styleDtos.map((style) => style.layerId),
      });

      const icons = iconsByNEType.get(neType);
      const icon = icons?.length ? icons[0] : null;

      const layerItem = new LayerItemV2({
        layerId: neType,
        layerName: neType,
        pane: neType,
        paneIndex: 1,
        layerDefinition,
        layerProvider,
        filtrableProperties,
        layerStyle,
        availableStyles,
        iconPath: icon?.iconSvg,
      });

      layerItems.push(layerItem);
    }

    if (persistencySettings) {
      layerItems = this._ctMapPersistencyService.applyLayerItemPersistency(
        layerItems,
        persistencySettings,
        currentThematics
      );
    } else {
      this._ctMapPersistencyService.createInitialPersistency(selectedWorkspace, layerItems);
    }

    return layerItems;
  }

  private getLayerProvider(): Observable<GeoserverLayerProviderSettings> {
    return this._authenticationService.getTenantName().pipe(
      map((tenantName) => {
        const geoServerUrl = this._settingsService.geoServer?.url;
        const layerProvider = new GeoserverLayerProviderSettings({
          url: `${geoServerUrl}/wlm/ows`,
          typeNS: 'wlm',
          typename: `${tenantName}-topography`,
        });

        return layerProvider;
      })
    );
  }

  private mapToGenericStyles(styleDtos: LayerStylesDto[]): MapGenericStyle[] {
    const styles = styleDtos.map((styleDto) => {
      const style: MapGenericStyle = {
        ...styleDto,
        styleDefinition: styleDto.styleDefinition as unknown as MapStyleDefinition,
      };
      return style;
    });
    return styles;
  }

  private buildSidePanel(): MapSidePanelSettings<any> {
    return {
      items: [],
    };
  }

  private buildTopBarConfiguration(params: BuildMapSettingsParams): MapTopbarButtonsConfiguration {
    return new MapTopbarButtonsConfiguration({
      buttonsComponent: CtTopBarButtonsComponent,
      params: {
        attributesMetadataChanged$: of(null), //this.tooltipMetadataChanged$,
        connectSettingsChanged$: this._selectedWorkspaceService.selectedWorkspace$.pipe(
          map((workspace) => workspace?.workspaceConnectSettings)
        ),
        stepChanged$: of(null), //this.stepChanged$,
        scenarioVisibilityChange$: params.scenarioVisibilityChange$,
        namespacesChange$: params.namespacesChange$,
      },
      featureInjector: CtMapModule.injector,
    });
  }

  private getAllScenarioIds = (connectSettings: WorkspaceConnectSettingDto[]) => {
    const scenarioIds = new Set<string>();
    connectSettings.forEach((item) => scenarioIds.add(item.scenarioId));
    return Array.from(scenarioIds.values());
  };

  private isEmptyNEType(elementTypeCategory: ElementTypeCategoriesDto): boolean {
    if (!elementTypeCategory.categories.length) {
      return true;
    }

    for (let category of elementTypeCategory.categories) {
      if (category.attributes.length) {
        return false;
      }
    }

    return true;
  }
}
